<script setup>
import { computed } from "vue";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip } from "@/components/ui/tooltip";
import { renderMarkdownInline } from "@utils/renderMarkdown";

const props = defineProps({
  populations: { type: Array, default: () => [] },
  matrixItems: { type: Array, default: () => [] },
  stackedData: { type: Array, default: () => [] },
  choiceCodes: { type: Array, default: () => [] },
  choiceLabels: { type: Object, default: () => ({}) },
  showSignificance: { type: Boolean, default: false },
  showCounts: { type: Boolean, default: true },
  clickable: { type: Boolean, default: false },
  questionId: { type: [Number, String], default: null },
});

const emit = defineEmits(["bar-click"]);

function onCellClick(event, questionId, code, popId) {
  if (!props.clickable || code === "999" || questionId == null) return;
  event.stopPropagation();
  emit("bar-click", { questionId, code, popId });
}

function cellClickable(code) {
  return props.clickable && code !== "999";
}

function fmtPct(value) {
  return `${Math.round(value * 100)}%`;
}

function sigIcon(item) {
  if (item.significance === "+") return "up";
  if (item.significance === "-") return "down";
  return null;
}

const isMatrix = computed(() => props.matrixItems.length > 0);
const isStacked = computed(() => props.stackedData.length > 0 && props.choiceCodes.length > 0);
</script>

<template>
  <div class="overflow-x-auto print:overflow-visible">
  <!-- Matrix table: items as group headers, choices as indented rows, populations as columns -->
  <Table v-if="isMatrix" class="text-xs ml-auto table-fixed" :style="{ width: `${Math.min(100, 40 + populations.length * 20)}%` }">
    <TableHeader>
      <TableRow>
        <TableHead class="h-8 px-2 text-xs font-medium w-full">Item</TableHead>
        <TableHead
          v-for="pop in populations"
          :key="pop.label"
          class="px-2 py-1.5 text-xs font-medium text-right w-[120px] align-bottom whitespace-normal"
        >
          <span class="line-clamp-2 text-right leading-tight">
            <span
              class="inline-block h-2 w-2 rounded-sm align-middle mr-1"
              :style="{ backgroundColor: pop.color }"
            />{{ pop.label }}
          </span>
        </TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      <template v-for="item in matrixItems" :key="item.itemLabel">
        <!-- Item header row -->
        <TableRow class="bg-muted/30">
          <TableCell :colspan="populations.length + 1" class="px-2 py-1.5 font-semibold text-xs">
            <Tooltip>
              <span class="block truncate">
                <span v-html="renderMarkdownInline(item.itemLabel)" />
                <span v-if="item.itemN != null" class="font-normal text-muted-foreground"> (N={{ item.itemN }})</span>
              </span>
              <template #content>
                <span v-html="renderMarkdownInline(item.itemLabel)" />
              </template>
            </Tooltip>
          </TableCell>
        </TableRow>
        <!-- Choice rows -->
        <TableRow v-for="choice in item.choices" :key="choice.code">
          <TableCell class="px-2 py-1 pl-5">
            <Tooltip>
              <span class="block truncate" v-html="renderMarkdownInline(choice.label)" />
              <template #content>
                <span v-html="renderMarkdownInline(choice.label)" />
              </template>
            </Tooltip>
          </TableCell>
          <TableCell
            v-for="(popValue, popIdx) in choice.populations"
            :key="popIdx"
            class="px-2 py-1 text-right tabular-nums w-[120px]"
            :class="cellClickable(choice.code) ? 'cursor-pointer' : ''"
            @click="onCellClick($event, item.itemId, choice.code, populations[popIdx]?.popId)"
          >
            <Tooltip :delay-duration="0">
              <span class="block">
                <span class="block">{{ fmtPct(popValue.value) }}</span>
                <span v-if="showCounts" class="block text-3xs text-muted-foreground">n={{ popValue.count }}</span>
              </span>
              <template #content>
                <div class="font-medium mb-1" v-html="renderMarkdownInline(item.itemLabel)" />
                <div class="flex items-center justify-between gap-4">
                  <span v-html="renderMarkdownInline(choice.label)" />
                  <span class="font-medium tabular-nums">
                    {{ fmtPct(popValue.value) }}
                    <span class="font-normal">(n={{ popValue.count }})</span>
                  </span>
                </div>
              </template>
            </Tooltip>
          </TableCell>
        </TableRow>
      </template>
    </TableBody>
  </Table>

  <!-- Stacked / matrix table -->
  <Table v-else-if="isStacked" class="text-xs ml-auto table-fixed" :style="{ width: `${Math.min(100, 40 + choiceCodes.length * 15)}%` }">
    <TableHeader>
      <TableRow>
        <TableHead class="h-8 px-2 text-xs font-medium w-full">Item</TableHead>
        <TableHead
          v-for="code in choiceCodes"
          :key="code"
          class="px-2 py-1.5 text-xs font-medium text-right w-[120px] align-bottom whitespace-normal"
        >
          <span class="line-clamp-2 text-right leading-tight">{{ choiceLabels[code] || code }}</span>
        </TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      <TableRow v-for="row in stackedData" :key="row.itemId">
        <TableCell class="px-2 py-1.5 font-medium">
          <Tooltip>
            <span class="block truncate" v-html="renderMarkdownInline(row.itemLabel)" />
            <template #content>
              <span v-html="renderMarkdownInline(row.itemLabel)" />
            </template>
          </Tooltip>
        </TableCell>
        <TableCell
          v-for="code in choiceCodes"
          :key="code"
          class="px-2 py-1.5 text-right tabular-nums w-[120px]"
          :class="cellClickable(code) ? 'cursor-pointer' : ''"
          @click="onCellClick($event, row.questionId, code, row.popId)"
        >
          <Tooltip :delay-duration="0">
            <span class="block">
              <span class="block">{{ fmtPct(row[`choice_${code}`] || 0) }}</span>
              <span v-if="showCounts" class="block text-3xs text-muted-foreground">n={{ row[`count_${code}`] || 0 }}</span>
            </span>
            <template #content>
              <div class="font-medium mb-1" v-html="renderMarkdownInline(row.itemLabel)" />
              <div class="flex items-center justify-between gap-4">
                <span>{{ choiceLabels[code] || code }}</span>
                <span class="font-medium tabular-nums">
                  {{ fmtPct(row[`choice_${code}`] || 0) }}
                  <span class="font-normal">(n={{ row[`count_${code}`] || 0 }})</span>
                </span>
              </div>
            </template>
          </Tooltip>
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>

  <!-- Simple (bar/pie) table with populations as columns -->
  <Table v-else-if="populations.length > 0" class="text-xs ml-auto table-fixed" :style="{ width: `${Math.min(100, 40 + populations.length * 20)}%` }">
    <TableHeader>
      <TableRow>
        <TableHead class="h-8 px-2 text-xs font-medium w-full">Response</TableHead>
        <TableHead
          v-for="pop in populations"
          :key="pop.label"
          class="px-2 py-1.5 text-xs font-medium text-right w-[120px] align-bottom whitespace-normal"
        >
          <span class="line-clamp-2 text-right leading-tight">
            <span
              class="inline-block h-2 w-2 rounded-sm align-middle mr-1"
              :style="{ backgroundColor: pop.color }"
            />{{ pop.label }}
          </span>
        </TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      <TableRow v-for="(item, idx) in populations[0]?.data || []" :key="item.code || idx">
        <TableCell class="px-2 py-1.5 font-medium">
          <Tooltip>
            <span class="block truncate" v-html="renderMarkdownInline(item.label)" />
            <template #content>
              <span v-html="renderMarkdownInline(item.label)" />
            </template>
          </Tooltip>
        </TableCell>
        <TableCell
          v-for="pop in populations"
          :key="pop.label"
          class="px-2 py-1.5 text-right tabular-nums w-[120px]"
          :class="cellClickable(item.code) ? 'cursor-pointer' : ''"
          @click="onCellClick($event, questionId, item.code, pop.popId)"
        >
          <Tooltip :delay-duration="0">
            <span class="block">
              <span
                v-if="showSignificance && sigIcon(pop.data[idx] || item)"
                class="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5"
                :class="sigIcon(pop.data[idx] || item) === 'up'
                  ? 'text-status-success-strong bg-status-success-soft'
                  : 'text-status-error-strong bg-status-error-soft'"
              >
                {{ fmtPct((pop.data[idx] || item).value) }}
                <span
                  v-if="sigIcon(pop.data[idx] || item) === 'up'"
                  class="text-3xs"
                >▲</span>
                <span
                  v-else
                  class="text-3xs"
                >▼</span>
              </span>
              <span v-else>{{ fmtPct((pop.data[idx] || item).value) }}</span>
              <span v-if="showCounts" class="block text-3xs text-muted-foreground">n={{ (pop.data[idx] || item).count }}</span>
            </span>
            <template #content>
              <div class="font-medium mb-1" v-html="renderMarkdownInline(item.label)" />
              <div class="flex items-center justify-between gap-4">
                <div class="flex items-center gap-1.5">
                  <div class="h-2.5 w-2.5 shrink-0 rounded-xs" :style="{ backgroundColor: pop.color }" />
                  <span>{{ pop.label }}</span>
                </div>
                <span class="font-medium tabular-nums">
                  {{ fmtPct((pop.data[idx] || item).value) }}
                  <span class="font-normal">(n={{ (pop.data[idx] || item).count }})</span>
                </span>
              </div>
            </template>
          </Tooltip>
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
  </div>
</template>
